import { useDisclosure, useElementSize } from "@mantine/hooks";
import cx from "classnames";
import type { Location } from "history";
import { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { replace } from "react-router-redux";
import { t } from "ttag";

import {
  useListTransformRunsQuery,
  useListTransformTagsQuery,
  useListTransformsQuery,
} from "metabase/api";
import { LoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper";
import { DataStudioBreadcrumbs } from "metabase/data-studio/common/components/DataStudioBreadcrumbs";
import { PaneHeader } from "metabase/data-studio/common/components/PaneHeader";
import { usePageTitle } from "metabase/hooks/use-page-title";
import { useDispatch } from "metabase/lib/redux";
import * as Urls from "metabase/lib/urls";
import { POLLING_INTERVAL } from "metabase/transforms/constants";
import { Center, Flex, Stack } from "metabase/ui";
import type { TransformRun, TransformRunId } from "metabase-types/api";

import { RunFilterBar } from "./RunFilterBar";
import S from "./RunListPage.module.css";
import { RunPagination } from "./RunPagination";
import { RunSidebar } from "./RunSidebar/RunSidebar";
import { RunTable } from "./RunTable";
import { PAGE_SIZE } from "./constants";
import type {
  TransformRunFilterOptions,
  TransformRunSortOptions,
} from "./types";
import {
  getFilterOptions,
  getParsedParams,
  getSortOptions,
  hasFilterOptions,
} from "./utils";

const EMPTY_RUNS: TransformRun[] = [];

type RunListPageProps = {
  location: Location;
};

export function RunListPage({ location }: RunListPageProps) {
  usePageTitle(t`Runs`);
  const params = getParsedParams(location);
  const { page = 0 } = params;
  const { ref: containerRef, width: containerWidth } = useElementSize();
  const [isResizing, { open: startResizing, close: stopResizing }] =
    useDisclosure();
  const [selectedRunId, setSelectedRunId] = useState<
    TransformRunId | undefined
  >();
  const [isPolling, setIsPolling] = useState(false);
  const dispatch = useDispatch();

  const {
    data,
    isLoading: isLoadingRuns,
    error: runsError,
  } = useListTransformRunsQuery(
    {
      offset: page * PAGE_SIZE,
      limit: PAGE_SIZE,
      statuses: params.statuses,
      "transform-ids": params.transformIds,
      "transform-tag-ids": params.transformTagIds,
      "start-time": params.startTime,
      "end-time": params.endTime,
      "run-methods": params.runMethods,
      "sort-column": params.sortColumn,
      "sort-direction": params.sortDirection,
    },
    {
      pollingInterval: isPolling ? POLLING_INTERVAL : undefined,
    },
  );

  const {
    data: transforms = [],
    isLoading: isLoadingTransforms,
    error: transformsError,
  } = useListTransformsQuery({});

  const {
    data: tags = [],
    isLoading: isLoadingTags,
    error: tagsError,
  } = useListTransformTagsQuery();

  const isLoading = isLoadingRuns || isLoadingTransforms || isLoadingTags;
  const error = runsError ?? transformsError ?? tagsError;

  if (isPolling !== isPollingNeeded(data?.data)) {
    setIsPolling(isPollingNeeded(data?.data));
  }

  const runs = data?.data ?? EMPTY_RUNS;

  const selectedRun = useMemo(
    () =>
      selectedRunId != null
        ? runs.find((run) => run.id === selectedRunId)
        : undefined,
    [selectedRunId, runs],
  );

  useLayoutEffect(() => {
    if (selectedRunId != null && selectedRun == null) {
      setSelectedRunId(undefined);
    }
  }, [selectedRunId, selectedRun]);

  const handleParamsChange = useCallback(
    (newParams: Urls.TransformRunListParams) => {
      dispatch(replace(Urls.transformRunList(newParams)));
    },
    [dispatch],
  );

  const handleFilterOptionsChange = useCallback(
    (filterOptions: TransformRunFilterOptions) => {
      handleParamsChange({ ...params, ...filterOptions, page: undefined });
    },
    [params, handleParamsChange],
  );

  const handleSortOptionsChange = useCallback(
    (sortOptions: TransformRunSortOptions | undefined) => {
      handleParamsChange({
        ...params,
        sortColumn: sortOptions?.column,
        sortDirection: sortOptions?.direction,
        page: undefined,
      });
    },
    [params, handleParamsChange],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      handleParamsChange({ ...params, page });
    },
    [params, handleParamsChange],
  );

  const handleSelect = useCallback((runId: TransformRunId) => {
    setSelectedRunId(runId);
  }, []);

  return (
    <Flex
      className={cx({ [S.resizing]: isResizing })}
      ref={containerRef}
      h="100%"
      wrap="nowrap"
      data-testid="transforms-run-list"
    >
      <Stack className={S.main} flex={1} px="3.5rem" pb="md" gap={0}>
        <PaneHeader
          breadcrumbs={<DataStudioBreadcrumbs>{t`Runs`}</DataStudioBreadcrumbs>}
          py={0}
          showMetabotButton
        />
        {!data || isLoading || error != null ? (
          <Center h="100%">
            <LoadingAndErrorWrapper loading={isLoading} error={error} />
          </Center>
        ) : (
          <Stack flex="0 1 auto" mih={0} gap="lg">
            <RunFilterBar
              filterOptions={getFilterOptions(params)}
              transforms={transforms}
              tags={tags}
              onFilterOptionsChange={handleFilterOptionsChange}
            />
            <RunTable
              runs={runs}
              tags={tags}
              hasFilters={hasFilterOptions(getFilterOptions(params))}
              sortOptions={getSortOptions(params)}
              onSortOptionsChange={handleSortOptionsChange}
              onSelect={handleSelect}
            />
            <RunPagination
              page={page}
              itemsLength={runs.length}
              totalCount={data.total}
              onPageChange={handlePageChange}
            />
          </Stack>
        )}
      </Stack>
      {selectedRun != null && (
        <RunSidebar
          run={selectedRun}
          containerWidth={containerWidth}
          onResizeStart={startResizing}
          onResizeStop={stopResizing}
          onClose={() => setSelectedRunId(undefined)}
        />
      )}
    </Flex>
  );
}

export function isPollingNeeded(runs: TransformRun[] = []) {
  return runs.some(
    (run) => run.status === "started" || run.status === "canceling",
  );
}
